import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiXMark, HiFlag } from 'react-icons/hi2';
import { API_BASE } from '../../config';
import CoachOptions from '../WorkoutDebrief/CoachOptions';
import { eventDates, formatShortDate } from '../../utils/plan_changes';
import { EVENT_SPORTS, EVENT_PRIORITIES, EVENT_INTENTS, EVENT_SPORT_EMOJI } from './eventMeta';

const MAX_EVENT_DAYS = 10;

const inputCls =
  'w-full border border-gray-200 rounded-xl px-3 py-2.5 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none text-sm font-medium';
const labelCls = 'block text-xs font-black text-gray-500 uppercase tracking-widest mb-2';

const toNumOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

export default function AddEventModal({ isOpen, onClose, planId, defaultSport = 'run', canEdit, onCreated, onApplied, plan = null }) {
  const { t, i18n } = useTranslation();
  const [title, setTitle] = useState('');
  const [sport, setSport] = useState(defaultSport);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dayValues, setDayValues] = useState({}); // { [iso]: { distance_km, duration_min, elevation_m } }
  const [priority, setPriority] = useState('B');
  const [intent, setIntent] = useState('complete');
  const [notes, setNotes] = useState('');
  const [replan, setReplan] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [createdEvent, setCreatedEvent] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setSport(['run', 'trail', 'bike', 'swim', 'triathlon'].includes(defaultSport) ? defaultSport : 'run');
    setStartDate('');
    setEndDate('');
    setDayValues({});
    setPriority('B');
    setIntent('complete');
    setNotes('');
    setReplan(true);
    setSubmitting(false);
    setError('');
    setCreatedEvent(null);
  }, [isOpen, defaultSport]);

  const effectiveEnd = endDate && endDate >= startDate ? endDate : startDate;
  const days = useMemo(
    () => (startDate ? eventDates({ start_date: startDate, end_date: effectiveEnd }) : []),
    [startDate, effectiveEnd]
  );
  const tooLong = days.length > MAX_EVENT_DAYS;
  const endBeforeStart = Boolean(endDate && startDate && endDate < startDate);
  const canSubmit = Boolean(canEdit && title.trim() && startDate && !tooLong && !endBeforeStart && !submitting);

  if (!isOpen) return null;

  const setDay = (iso, key, value) => {
    setDayValues((prev) => ({ ...prev, [iso]: { ...(prev[iso] || {}), [key]: value } }));
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        event: {
          title: title.trim(),
          sport,
          start_date: startDate,
          end_date: effectiveEnd,
          days: days.map((iso) => ({
            date: iso,
            distance_km: toNumOrNull(dayValues[iso]?.distance_km),
            duration_min: toNumOrNull(dayValues[iso]?.duration_min),
            elevation_m: toNumOrNull(dayValues[iso]?.elevation_m)
          })),
          priority,
          intent,
          notes: notes.trim() || null
        },
        replan
      };
      const res = await fetch(`${API_BASE}/plans/${planId}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || t('events.add_error'));
      onCreated?.(data?.event || null);
      if (replan && data?.event?.replan) {
        setCreatedEvent(data.event);
      } else {
        onClose?.();
      }
    } catch (e) {
      setError(e?.message || t('events.add_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4 z-50 animate-fade-in">
      <div className={`bg-white w-full ${createdEvent ? 'md:max-w-4xl' : 'md:max-w-2xl'} rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] md:max-h-[90vh] flex flex-col border border-gray-100 animate-slide-in-bottom md:animate-none`}>
        <div className="p-5 md:p-6 border-b border-gray-100 flex justify-between items-start gap-4 bg-white">
          <div className="min-w-0">
            <h3 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight leading-tight flex items-center gap-2">
              <HiFlag className="w-6 h-6 text-indigo-500" />
              {createdEvent ? createdEvent.title : t('events.add_title')}
            </h3>
            <div className="text-xs font-bold text-gray-400 mt-1">
              {createdEvent
                ? `${formatShortDate(createdEvent.start_date, i18n.language)}${createdEvent.end_date && createdEvent.end_date !== createdEvent.start_date ? ` → ${formatShortDate(createdEvent.end_date, i18n.language)}` : ''}`
                : t('events.add_subtitle')}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors shrink-0"
            aria-label={t('workout.close')}
          >
            <HiXMark className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5 md:p-6 overflow-y-auto">
          {createdEvent ? (
            <CoachOptions
              analysis={createdEvent.replan}
              planId={planId}
              source="event"
              sourceId={createdEvent.id}
              canEdit={canEdit}
              onApplied={onApplied}
              plan={plan}
            />
          ) : submitting && replan ? (
            <div className="py-14 flex flex-col items-center text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-100 border-t-indigo-600 mb-5" />
              <div className="text-lg font-black text-gray-900">{t('events.replan_loading')}</div>
              <div className="text-sm text-gray-500 mt-1 max-w-sm">{t('events.replan_loading_hint')}</div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className={labelCls}>{t('events.field_title')}</label>
                <input
                  type="text"
                  maxLength={120}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('events.field_title_placeholder')}
                  className={inputCls}
                />
              </div>

              <div>
                <label className={labelCls}>{t('events.field_sport')}</label>
                <div className="flex flex-wrap gap-2">
                  {EVENT_SPORTS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSport(s)}
                      className={[
                        'px-3 py-2 rounded-xl border-2 text-sm font-bold transition-all inline-flex items-center gap-1.5',
                        sport === s ? 'border-indigo-500 bg-indigo-50 text-indigo-800' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      ].join(' ')}
                      aria-pressed={sport === s}
                    >
                      <span>{EVENT_SPORT_EMOJI[s]}</span> {t(`events.sport.${s}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t('events.field_start')}</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setStartDate(v);
                      if (!endDate || endDate < v) setEndDate(v);
                    }}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('events.field_end')}</label>
                  <input
                    type="date"
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>
              {tooLong ? (
                <div className="text-xs font-bold text-red-600">{t('events.too_long', { max: MAX_EVENT_DAYS })}</div>
              ) : null}
              {endBeforeStart ? (
                <div className="text-xs font-bold text-red-600">{t('events.end_before_start')}</div>
              ) : null}

              {days.length > 0 && !tooLong ? (
                <div>
                  <label className={labelCls}>{t('events.field_days')}</label>
                  <div className="space-y-2">
                    {days.map((iso, idx) => (
                      <div key={iso} className="rounded-2xl border border-gray-100 bg-gray-50/60 p-3">
                        <div className="text-xs font-black text-gray-700 mb-2">
                          {days.length > 1 ? `${t('events.day_n', { n: idx + 1 })} · ` : ''}{formatShortDate(iso, i18n.language)}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { key: 'distance_km', label: t('events.day_distance'), step: '0.1' },
                            { key: 'duration_min', label: t('events.day_duration'), step: '1' },
                            { key: 'elevation_m', label: t('events.day_elevation'), step: '10' }
                          ].map((f) => (
                            <div key={f.key}>
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">{f.label}</div>
                              <input
                                type="number"
                                min="0"
                                step={f.step}
                                inputMode="decimal"
                                value={dayValues[iso]?.[f.key] ?? ''}
                                onChange={(e) => setDay(iso, f.key, e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-2 py-2 bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none text-sm font-bold text-center tabular-nums"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div>
                <label className={labelCls}>{t('events.field_priority')}</label>
                <div className="grid gap-2 sm:grid-cols-3">
                  {EVENT_PRIORITIES.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPriority(p)}
                      className={[
                        'text-left p-3 rounded-xl border-2 transition-all',
                        priority === p ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white hover:border-gray-300'
                      ].join(' ')}
                      aria-pressed={priority === p}
                    >
                      <div className="text-sm font-black text-gray-900">{t(`events.priority.${p}.label`)}</div>
                      <div className="text-xs text-gray-500 mt-0.5 leading-snug">{t(`events.priority.${p}.hint`)}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>{t('events.field_intent')}</label>
                <div className="flex bg-gray-100 p-1.5 rounded-xl">
                  {EVENT_INTENTS.map((it) => (
                    <button
                      key={it}
                      type="button"
                      onClick={() => setIntent(it)}
                      className={`flex-1 py-2 px-1 rounded-lg font-bold text-sm transition-all ${intent === it ? 'bg-white shadow-sm text-indigo-600 ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-700'}`}
                      aria-pressed={intent === it}
                    >
                      {t(`events.intent.${it}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>
                  {t('events.field_notes')} <span className="normal-case tracking-normal text-gray-400 font-bold">({t('workout.optional')})</span>
                </label>
                <textarea
                  rows={3}
                  maxLength={600}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('events.field_notes_placeholder')}
                  className={`${inputCls} resize-none`}
                />
              </div>

              <label className="flex items-start gap-3 p-4 rounded-2xl border border-indigo-100 bg-indigo-50/60 cursor-pointer">
                <input
                  type="checkbox"
                  checked={replan}
                  onChange={(e) => setReplan(e.target.checked)}
                  className="mt-0.5 w-5 h-5 accent-indigo-600"
                />
                <span>
                  <span className="block text-sm font-black text-indigo-900">{t('events.replan_checkbox')}</span>
                  <span className="block text-xs text-indigo-700 mt-0.5">{t('events.replan_checkbox_hint')}</span>
                </span>
              </label>

              {error ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700">{error}</div>
              ) : null}
            </div>
          )}
        </div>

        <div className="p-4 md:p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3">
          {createdEvent ? (
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2.5 rounded-full font-bold text-sm text-gray-700 bg-white border border-gray-200 hover:bg-gray-100"
            >
              {t('workout.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-200 rounded-full transition-colors text-sm"
              >
                {t('workout.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={`px-6 py-2.5 rounded-full font-black text-sm text-white shadow-lg transition-all ${
                  canSubmit ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/30 hover:-translate-y-0.5' : 'bg-gray-300 shadow-none cursor-not-allowed'
                }`}
              >
                {submitting ? t('events.adding') : t('events.add_submit')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
